/**
 * Input Sanitization Utilities
 *
 * Protects against prompt injection attacks by sanitizing user input
 * before it's sent to AI models.
 */

/**
 * Patterns that could be used for prompt injection
 */
const INJECTION_PATTERNS = [
  /```[\s\S]*?```/g, // Code blocks
  /<\/?[a-z][\s\S]*?>/gi, // HTML tags
  /\[\[.*?\]\]/g, // Wiki-style links
  /\{\{.*?\}\}/g, // Template literals
  /ignore\s+(all\s+)?previous\s+(instructions?|prompts?)/gi,
  /disregard\s+(all\s+)?(previous\s+)?(instructions?|prompts?)/gi,
  /forget\s+(all\s+)?(previous\s+)?(instructions?|prompts?)/gi,
  /you\s+are\s+now\s+/gi,
  /new\s+instructions?:/gi,
  /system\s*:\s*/gi,
  /assistant\s*:\s*/gi,
  /human\s*:\s*/gi,
  /user\s*:\s*/gi,
  /<\|.*?\|>/g, // Special tokens
  /\[INST\]/gi, // Instruction markers
  /\[\/INST\]/gi,
]

/**
 * Maximum lengths for different field types
 */
export const MAX_LENGTHS = {
  name: 50,
  shortText: 200,
  mediumText: 500,
  longText: 1000,
  intentionStatement: 300,
  reflection: 2000,
} as const

/**
 * Sanitize a single text input
 *
 * @param input - The text to sanitize
 * @param maxLength - Maximum allowed length (default: 500)
 * @returns Sanitized text
 */
export function sanitizeText(
  input: string | undefined | null,
  maxLength: number = MAX_LENGTHS.mediumText
): string {
  if (!input || typeof input !== 'string') return ''

  let sanitized = input.trim()

  // Truncate to max length first
  if (sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength)
  }

  // Remove potential injection patterns
  for (const pattern of INJECTION_PATTERNS) {
    sanitized = sanitized.replace(pattern, '')
  }

  // Normalize whitespace (collapse multiple spaces/newlines)
  sanitized = sanitized.replace(/\s+/g, ' ')

  // Remove any remaining control characters except newlines
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

  return sanitized.trim()
}

/**
 * Sanitize text but preserve newlines for multi-line inputs
 *
 * @param input - The text to sanitize
 * @param maxLength - Maximum allowed length
 * @returns Sanitized text with preserved newlines
 */
export function sanitizeMultilineText(
  input: string | undefined | null,
  maxLength: number = MAX_LENGTHS.longText
): string {
  if (!input || typeof input !== 'string') return ''

  let sanitized = input.trim()

  // Truncate to max length first
  if (sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength)
  }

  // Remove potential injection patterns
  for (const pattern of INJECTION_PATTERNS) {
    sanitized = sanitized.replace(pattern, '')
  }

  // Normalize multiple newlines to max 2
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n')

  // Collapse multiple spaces (but not newlines)
  sanitized = sanitized.replace(/[^\S\n]+/g, ' ')

  // Remove control characters except newlines and tabs
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

  return sanitized.trim()
}

/**
 * Sanitize an array of strings
 *
 * @param arr - Array of strings to sanitize
 * @param maxLength - Maximum length per item
 * @param maxItems - Maximum number of items
 * @returns Sanitized array
 */
export function sanitizeArray(
  arr: string[] | undefined | null,
  maxLength: number = MAX_LENGTHS.shortText,
  maxItems: number = 20
): string[] {
  if (!arr || !Array.isArray(arr)) return []

  return arr
    .slice(0, maxItems)
    .map(item => sanitizeText(item, maxLength))
    .filter(Boolean)
}

/**
 * Sanitize a name field (stricter rules)
 *
 * @param name - The name to sanitize
 * @returns Sanitized name
 */
export function sanitizeName(name: string | undefined | null): string {
  if (!name || typeof name !== 'string') return ''

  // Only allow letters, spaces, hyphens, apostrophes, and common unicode letters
  let sanitized = name
    .trim()
    .slice(0, MAX_LENGTHS.name)
    .replace(/[^\p{L}\p{M}\s\-']/gu, '')
    .replace(/\s+/g, ' ')

  return sanitized.trim()
}

/**
 * Deep sanitize an object for API submission
 * Recursively sanitizes all string values in an object
 *
 * @param obj - Object to sanitize
 * @param maxStringLength - Maximum length for string values
 * @returns Sanitized object
 */
export function sanitizeRequestBody<T extends Record<string, unknown>>(
  obj: T,
  maxStringLength: number = MAX_LENGTHS.longText
): T {
  if (!obj || typeof obj !== 'object') return obj

  const sanitized = { ...obj }

  for (const [key, value] of Object.entries(sanitized)) {
    if (typeof value === 'string') {
      // Use appropriate sanitizer based on key name
      if (key === 'name' || key === 'userName') {
        ;(sanitized as Record<string, unknown>)[key] = sanitizeName(value)
      } else if (
        key.includes('reflection') ||
        key.includes('context') ||
        key.includes('description')
      ) {
        ;(sanitized as Record<string, unknown>)[key] = sanitizeMultilineText(
          value,
          maxStringLength
        )
      } else {
        ;(sanitized as Record<string, unknown>)[key] = sanitizeText(value, maxStringLength)
      }
    } else if (Array.isArray(value)) {
      ;(sanitized as Record<string, unknown>)[key] = value.map(item => {
        if (typeof item === 'string') {
          return sanitizeText(item, MAX_LENGTHS.mediumText)
        }
        if (item && typeof item === 'object') {
          return sanitizeRequestBody(item as Record<string, unknown>, maxStringLength)
        }
        return item
      })
    } else if (value && typeof value === 'object') {
      ;(sanitized as Record<string, unknown>)[key] = sanitizeRequestBody(
        value as Record<string, unknown>,
        maxStringLength
      )
    }
  }

  return sanitized
}

/**
 * Escape special characters for safe interpolation into prompts
 * Use this for values that need to be quoted in prompts
 *
 * @param input - The text to escape
 * @returns Escaped text safe for prompt interpolation
 */
export function escapeForPrompt(input: string | undefined | null): string {
  if (!input || typeof input !== 'string') return ''

  return input
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
}
